import {Modal, ModalBody, ModalContent, ModalHeader} from "@heroui/react";
import {addToast} from "@heroui/toast";
import {Icon} from "@iconify/react";
import {useState} from "react";

import {config} from "../config/config.ts";
import {startGoogleSignIn} from "../utilities/googleAuth.ts";
import {useJwtAuth} from "../JWTAuthProvider.tsx";
import {SignupComponent} from "../signup/SignupView.tsx";
import PButton from "./PButton.tsx";
import PInput from "./PInput.tsx";

// Shared with the event page so a Google redirect can resume the intended action.
export const PENDING_ACTION_KEY = 'pending_event_action';

const EventAuthModal = ({
    open,
    onClose,
    onAuthenticated,
    eventTitle,
    uid,
    action,
    presetSport,
    presetEmail,
    startMode = 'login',
}: {
    open: boolean;
    onClose: () => void;
    onAuthenticated: () => void;
    eventTitle?: string;
    uid: string;
    action: string;
    /** Known up front when joining something specific (e.g. a club invite) — skips the wizard. */
    presetSport?: string | null;
    presetEmail?: string | null;
    startMode?: 'login' | 'signup';
}) => {
    const jwt = useJwtAuth();
    const [mode, setMode] = useState<'login' | 'signup'>(startMode);
    const [email, setEmail] = useState(presetEmail || '');
    const [password, setPassword] = useState('');
    const [isVisible, setIsVisible] = useState(false);
    const [busy, setBusy] = useState(false);

    const login = async (e: any) => {
        e.preventDefault();
        setBusy(true);
        const res = await jwt.signInWithEmailAndPassword(email, password);
        setBusy(false);
        if (res?.user) {
            onAuthenticated();
        } else {
            addToast({color: 'danger', title: 'Wrong email or password'});
        }
    };

    // Google sign-in needs a redirect, so remember the action and resume it on return — on THIS
    // page: the sign-in lands on /login/jwt-redirect, which otherwise sends them to their home,
    // and a page reached by an emailed link (connect a number, accept an invite) was lost, with
    // the sign-in to do again from the link. The event page reads the pending action as well.
    const google = () => {
        sessionStorage.setItem(PENDING_ACTION_KEY, JSON.stringify({uid, action}));
        startGoogleSignIn(window.location.pathname + window.location.search);
    };

    return (
        <Modal isOpen={open} placement="top" onOpenChange={(o) => {
            if (!o) onClose();
        }}>
            <ModalContent>
                <ModalHeader className="flex flex-col gap-0.5">
                    <span>{mode === 'login' ? 'Log in to continue' : 'Create your account'}</span>
                    {eventTitle ? <span className="text-sm font-normal text-default-500">{eventTitle}</span> : null}
                </ModalHeader>
                <ModalBody className="pb-6">
                    {mode === 'login' ? (
                        <>
                            <form onSubmit={login} className="flex flex-col gap-3">
                                <PInput label="Email" type="email" autoComplete="email" name="email"
                                        value={email} onChange={(e) => setEmail(e.target.value)} required />
                                <PInput label="Password" type={isVisible ? 'text' : 'password'} name="password"
                                        autoComplete="current-password" value={password}
                                        onChange={(e) => setPassword(e.target.value)} required
                                        endContent={
                                            <button type="button" aria-label="toggle password visibility"
                                                    className="focus:outline-none" onClick={() => setIsVisible(!isVisible)}>
                                                <Icon icon={isVisible ? 'mdi-light:eye-off' : 'mdi-light:eye'} className="text-xl" />
                                            </button>
                                        } />
                                <PButton type="submit" color="primary" className="w-full rounded-full" isLoading={busy}>
                                    Log in
                                </PButton>
                            </form>

                            {!config.isCordova && (
                                <PButton variant="bordered" className="w-full rounded-full" onPress={google}
                                         startContent={<Icon icon="mdi:google" className="text-2xl" />}>
                                    Continue with Google
                                </PButton>
                            )}

                            <div className="text-center text-sm text-default-500">
                                New to ScorePal?{' '}
                                <button type="button" className="underline text-primary" onClick={() => setMode('signup')}>Create an account</button>
                            </div>
                        </>
                    ) : (
                        <>
                            <SignupComponent onComplete={onAuthenticated} playerOnly
                                             presetSport={presetSport} presetEmail={presetEmail} />
                            <div className="mt-2 text-center text-sm text-default-500">
                                Already have an account?{' '}
                                <button type="button" className="underline text-primary" onClick={() => setMode('login')}>Log in</button>
                            </div>
                        </>
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default EventAuthModal;
